/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch, FaTrash, FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import ConfirmModal from '../common/ConfirmModal';
import './TestimonialList.css';

const TestimonialList = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/testimonials');
      if (res && res.data && Array.isArray(res.data)) {
        setTestimonials(res.data);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleStatusToggle = async (id, currentStatus) => {
    const nextStatus = currentStatus === 1 ? 0 : 1;
    try {
      await axiosInstance.put(`/testimonials/${id}`, { status: nextStatus });
      setTestimonials(prev => prev.map(t => t.id === id ? { ...t, status: nextStatus } : t));
      toast.success('Status updated successfully!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = (testimonial) => {
    setDeleteTarget(testimonial);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await axiosInstance.delete(`/testimonials/${deleteTarget.id}`);
      setTestimonials(prev => prev.filter(t => t.id !== deleteTarget.id));
      toast.success('Testimonial deleted successfully!');
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete testimonial');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmEdit = () => {
    if (editTarget) {
      const id = editTarget.id;
      setEditTarget(null);
      navigate(`/edit-testimonial/${id}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '10/06/2025 12:53 PM';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; 
    const formattedHours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const getInitialsAvatar = (name) => {
    if (!name) return { text: '?', bg: '#10b981' };
    const parts = name.trim().split(' ');
    const firstLetter = parts[0] ? parts[0][0].toUpperCase() : '';
    const secondLetter = parts[1] ? parts[1][0].toUpperCase() : '';
    const text = firstLetter + secondLetter;
    
    const colors = [
      '#10b981', // green
      '#f97316', // orange
      '#a855f7', // purple
      '#065f46', // dark green
      '#3b82f6', // blue
      '#ec4899', // pink
      '#eab308'  // yellow
    ];
    const code = firstLetter.charCodeAt(0) || 0;
    const bg = colors[code % colors.length];
    
    return { text, bg };
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    const backendHost = process.env.REACT_APP_BACKEND 
      ? process.env.REACT_APP_BACKEND.replace(/\/$/, '') 
      : (process.env.REACT_APP_BACKEND_URL || '').split('/api')[0] || 'http://localhost:8000';
    return `${backendHost}/uploads/${image}`;
  };

  const filteredTestimonials = testimonials.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.review.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTestimonials = filteredTestimonials.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <div className="premium-page-container animate-fade-up">
        
        {/* Header Section */}
        <div className="premium-page-header">
          <h1 className="page-title">Manage Testimonials</h1>
          <button className="btn-create" onClick={() => navigate('/add-testimonial')}>
            <FaPlus style={{ marginRight: '8px' }} /> Create new
          </button>
        </div>

        {/* Filter Panel */}
        <div className="premium-controls-bar">
          <div className="search-bar-wrapper search-box" style={{ margin: 0 }}>
            <FaSearch className="search-icon" />
            <input 
              type="text" 
              placeholder="Search here" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="premium-table-wrapper">
          {loading ? (
            <div className="loading-spinner" style={{ padding: '2rem', textAlign: 'center', color: '#c4a882' }}>Loading testimonials...</div>
          ) : filteredTestimonials.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem', textAlign: 'center', color: '#c4a882' }}>No testimonials found.</div>
          ) : (
            <table className="premium-table">
              <thead>
                <tr>
                  <th style={{ width: '4%', textAlign: 'center' }}>
                    <input type="checkbox" className="premium-checkbox" readOnly />
                  </th>
                  <th style={{ width: '12%' }}>Status</th>
                  <th style={{ width: '8%' }}>Image</th>
                  <th style={{ width: '18%' }}>Name</th>
                  <th style={{ width: '34%' }}>Testimonial</th>
                  <th style={{ width: '14%' }}>Created on</th>
                  <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentTestimonials.map(t => {
                  const avatar = getInitialsAvatar(t.name);
                  return (
                    <tr key={t.id}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" className="premium-checkbox" readOnly />
                      </td>
                      <td>
                        <div className="status-toggle-wrapper">
                          <label className="switch-toggle" style={{ transform: 'scale(0.85)', marginBottom: 0 }}>
                            <input 
                              type="checkbox" 
                              checked={t.status === 1 || t.status === '1'}
                              onChange={() => handleStatusToggle(t.id, t.status)}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <span className={`status-label ${t.status === 1 ? 'active' : 'inactive'}`}>
                            {t.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td>
                        {t.user_image ? (
                          <div className="customer-avatar-wrapper">
                            <img src={getImageUrl(t.user_image)} alt={t.name} />
                          </div>
                        ) : (
                          <div 
                            className="customer-avatar-wrapper initials" 
                            style={{ backgroundColor: avatar.bg }}
                          >
                            {avatar.text}
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: '600', color: '#ffffff' }}>
                        {t.name}
                        {t.location && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'normal', marginTop: '0.15rem' }}>
                            {t.location}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="text-truncate-cell" title={t.review} style={{ color: '#94a3b8', fontSize: '0.9rem', lineHeight: '1.5' }}>
                          {t.review}
                        </div>
                      </td>
                      <td className="nowrap-cell" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        {formatDate(t.created_on || t.createdAt)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="action-buttons-cell" style={{ justifyContent: 'center', display: 'flex', gap: '0.5rem' }}>
                          <button 
                            type="button" 
                            className="btn-action-icon edit-icon"
                            onClick={() => setEditTarget(t)}
                            title="Edit"
                            style={{ background: 'none', border: 'none', color: '#c4a882', cursor: 'pointer' }}
                          >
                            <FaEdit />
                          </button>
                          <button 
                            type="button" 
                            className="btn-action-icon delete-icon"
                            onClick={() => handleDelete(t)}
                            title="Delete"
                            style={{ background: 'none', border: 'none', color: '#ea580c', cursor: 'pointer' }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination Controls */}
          {filteredTestimonials.length > 0 && (
            <div className="premium-pagination-container" style={{ margin: '1.5rem 0 0 0' }}>
              <div className="premium-pagination-info">
                Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredTestimonials.length)}</span> of <span>{filteredTestimonials.length}</span> entries
              </div>
              <div className="premium-pagination-list">
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(1)} 
                  disabled={currentPage === 1}
                >
                  &laquo;
                </button>
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                  disabled={currentPage === 1}
                >
                  &lsaquo;
                </button>
                
                {Array.from({ length: Math.ceil(filteredTestimonials.length / itemsPerPage) }, (_, idx) => idx + 1)
                  .filter(page => {
                    const totalPages = Math.ceil(filteredTestimonials.length / itemsPerPage);
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => {
                    return (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                        )}
                        <button 
                          className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredTestimonials.length / itemsPerPage)))} 
                  disabled={currentPage === Math.ceil(filteredTestimonials.length / itemsPerPage)}
                >
                  &rsaquo;
                </button>
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(Math.ceil(filteredTestimonials.length / itemsPerPage))} 
                  disabled={currentPage === Math.ceil(filteredTestimonials.length / itemsPerPage)}
                >
                  &raquo;
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Edit Confirmation Modal */}
      <ConfirmModal
        isOpen={!!editTarget}
        type="edit"
        title="Modify Testimonial"
        itemName={editTarget?.name}
        itemType="testimonial"
        onConfirm={handleConfirmEdit}
        onCancel={() => setEditTarget(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        type="delete"
        title="Delete Testimonial"
        itemName={deleteTarget?.name}
        itemType="testimonial"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default TestimonialList;
